import React, { useEffect, useState } from "react";
import { Eye, Save, Search, X, History as HistoryIcon } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader, SectionCard } from "../components/Common";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const EYE_FIELDS = [
  ["va_uncorrected", "VA uncorrected", "6/12"],
  ["va_corrected",   "VA corrected",   "6/6"],
  ["sph",            "Sphere (SPH)",   "-2.50"],
  ["cyl",            "Cylinder (CYL)", "-0.75"],
  ["axis",           "Axis",           "90"],
  ["add",            "Add",            "+2.00"],
  ["iop",            "IOP (mmHg)",     "16"],
];

function EyeCard({ side, values, onChange, testPrefix }) {
  const set = (k, v) => onChange({ ...values, [k]: v });
  const label = side === "OD" ? "Right Eye (OD)" : "Left Eye (OS)";
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white" data-testid={`${testPrefix}-card`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${side === "OD" ? "bg-[#1976D2]" : "bg-[#0EA5B7]"}`}>{side}</div>
        <div className="font-semibold text-slate-800">{label}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {EYE_FIELDS.map(([k, l, ph]) => (
          <label key={k} className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</span>
            <input
              data-testid={`${testPrefix}-${k}`}
              value={values[k] || ""}
              onChange={(e) => set(k, e.target.value)}
              placeholder={ph}
              className="mt-1 w-full h-9 px-2 rounded-md border border-slate-200 text-sm tabular"
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function TA({ label, value, onChange, testId, rows = 3 }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-2 py-2 rounded-md border border-slate-200 text-sm"
      />
    </label>
  );
}

function Fld({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
      />
    </label>
  );
}

const blankEye = {};

export default function Optical() {
  const { organization } = useAuth();
  const enabled = organization?.features?.optical;
  const [patient, setPatient] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [OD, setOD] = useState(blankEye);
  const [OS, setOS] = useState(blankEye);
  const [chief, setChief] = useState("");
  const [findings, setFindings] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [plan, setPlan] = useState("");
  const [rx, setRx] = useState("");
  const [frame, setFrame] = useState("");
  const [lens, setLens] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  if (!enabled) return <Navigate to="/app" replace />;

  const search = async (v) => {
    setQ(v);
    if (v.length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/patients?q=${encodeURIComponent(v)}&limit=8`);
      setResults(data || []);
    } catch { setResults([]); }
  };

  const loadHistory = async (pid) => {
    try { const { data } = await api.get(`/speciality/optical/exams?patient_id=${pid}`); setHistory(data || []); }
    catch { setHistory([]); }
  };

  const pick = (p) => { setPatient(p); setResults([]); setQ(""); loadHistory(p.patient_id); };
  const clearPatient = () => { setPatient(null); setHistory([]); };

  const resetForm = () => { setOD(blankEye); setOS(blankEye); setChief(""); setFindings(""); setDiagnosis(""); setPlan(""); setRx(""); setFrame(""); setLens(""); };

  const save = async () => {
    if (!patient) return toast.error("Pick a patient first");
    setSaving(true);
    try {
      await api.post("/speciality/optical/exams", {
        patient_id: patient.patient_id,
        right_eye: OD, left_eye: OS,
        chief_complaint: chief, findings, diagnosis, plan,
        prescription: rx, dispensed_frame: frame, dispensed_lens: lens,
      });
      toast.success("Optical exam saved");
      resetForm();
      loadHistory(patient.patient_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="optical-page">
      <PageHeader
        title="Optical Centre"
        subtitle={`Refraction · Prescription · Dispensing · ${organization?.name || ""}`}
      />

      {!patient ? (
        <SectionCard title="Select a patient" testId="opt-patient-picker">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="opt-patient-search"
              value={q}
              onChange={(e) => search(e.target.value)}
              placeholder="Search by name / Patient ID / phone"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          {results.length > 0 && (
            <div className="mt-2 rounded-lg border border-slate-200 divide-y max-w-md bg-white">
              {results.map((p) => (
                <button
                  key={p.patient_id}
                  onClick={() => pick(p)}
                  data-testid={`opt-pick-${p.patient_id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between"
                >
                  <span className="text-sm font-medium">{p.first_name} {p.last_name}</span>
                  <span className="text-xs text-slate-500 tabular">{p.ths_id}</span>
                </button>
              ))}
            </div>
          )}
          {q.length >= 2 && results.length === 0 && (
            <div className="mt-2 text-xs text-slate-500">No patients matched "{q}"</div>
          )}
        </SectionCard>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid="opt-patient-banner">
          <div>
            <div className="font-semibold text-slate-900">{patient.first_name} {patient.last_name}</div>
            <div className="text-xs text-slate-500 tabular">{patient.ths_id} · {patient.phone || "no phone"} · {patient.gender || "—"}</div>
          </div>
          <button onClick={clearPatient} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><X className="w-3 h-3" /> Change patient</button>
        </div>
      )}

      {patient && (
        <>
          <SectionCard title="Refraction & visual acuity" testId="opt-refraction">
            <div className="grid md:grid-cols-2 gap-4">
              <EyeCard side="OD" values={OD} onChange={setOD} testPrefix="opt-od" />
              <EyeCard side="OS" values={OS} onChange={setOS} testPrefix="opt-os" />
            </div>
          </SectionCard>

          <SectionCard title="Clinical notes" testId="opt-notes">
            <div className="grid md:grid-cols-2 gap-3">
              <TA label="Chief complaint" value={chief} onChange={setChief} testId="opt-chief" />
              <TA label="Findings" value={findings} onChange={setFindings} testId="opt-findings" />
              <TA label="Diagnosis" value={diagnosis} onChange={setDiagnosis} testId="opt-dx" />
              <TA label="Plan" value={plan} onChange={setPlan} testId="opt-plan" />
            </div>
          </SectionCard>

          <SectionCard title="Prescription & dispensing" testId="opt-rx">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <TA label="Final Rx (freeform)" value={rx} onChange={setRx} testId="opt-final-rx" rows={4} />
              </div>
              <Fld label="Frame dispensed" value={frame} onChange={setFrame} placeholder="e.g. Titanium ML-234" testId="opt-frame" />
              <Fld label="Lens dispensed" value={lens} onChange={setLens} placeholder="e.g. Photochromic 1.56 AR" testId="opt-lens" />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                data-testid="opt-save"
                onClick={save}
                disabled={saving}
                className="px-5 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save exam"}
              </button>
            </div>
          </SectionCard>

          <SectionCard
            title={<span className="flex items-center gap-2"><HistoryIcon className="w-4 h-4" /> Previous exams · {history.length}</span>}
            testId="opt-history"
          >
            {history.length === 0 ? (
              <div className="text-sm text-slate-500 p-6 text-center">No prior optical exams recorded for this patient.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2">Date</th>
                      <th className="text-left">Diagnosis</th>
                      <th className="text-left">OD · SPH / CYL / AXIS</th>
                      <th className="text-left">OS · SPH / CYL / AXIS</th>
                      <th className="text-left">Rx</th>
                      <th className="text-left">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {history.map((h) => {
                      const od = h.right_eye || {}; const os = h.left_eye || {};
                      const j = (e) => [e.sph, e.cyl, e.axis].filter(Boolean).join(" / ") || "—";
                      return (
                        <tr key={h.exam_id} className="hover:bg-slate-50" data-testid={`opt-row-${h.exam_id}`}>
                          <td className="py-2 tabular text-slate-600">{new Date(h.created_at).toLocaleDateString()}</td>
                          <td className="max-w-[220px] truncate">{h.diagnosis || "—"}</td>
                          <td className="tabular text-xs">{j(od)}</td>
                          <td className="tabular text-xs">{j(os)}</td>
                          <td className="max-w-[180px] truncate text-xs text-slate-600">{h.prescription || "—"}</td>
                          <td className="text-xs text-slate-500">{h.recorded_by_name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
